import React, { FC, useState, ChangeEvent, useEffect, useContext, Fragment } from 'react';
import { Box, Paper, TextField, Typography, Divider, Button, Alert } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ChatMessageContent from './chat-message-content.component';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, ApolloError, ApolloCache, Reference } from '@apollo/client';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { sendChatMessageTemplate, chatMessagesTemplate, markChatMessageAsReadTemplate } from 'apollo/templates/message';
import { getEscapedMessage } from 'utils/escaped-message';
import { animateScroll } from 'react-scroll';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { customerSettings } from 'utils/customerSettings';
import ChatTemplateDialog from '../chat-template-dialog/chat-template-dialog.component';
import { ChatMessage } from 'models/chat-messages';
import { InvoicingState, Order } from 'models/order';
import { socketIo } from 'context/socket';

type ChatMessagesProps = {
    order: Order;
    isSupplier: boolean;
    isCustomer: boolean;
    isExternalTechnician: boolean;
};

const ChatMessages: FC<ChatMessagesProps> = ({
    order,
    isSupplier,
    isCustomer,
    isExternalTechnician,
}: ChatMessagesProps) => {
    const { t: translate } = useTranslation(['messages']);
    const [noOfMessageRows, setNoOfMessageRows] = useState(1);
    const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
    const [messageValue, setMessageValue] = useState('');
    const [showChatTemplateBtn, setShowChatTemplateBtn] = useState(false);
    const [showChatTemplateDialog, setShowChatTemplateDialog] = useState(false);
    const [errorMsg, setErrorMsg] = useState<string>('');
    const currentOrganization = currentOrgVar();
    const socket = useContext(socketIo.getSocketContext());
    const { showBoundary } = useDenthubErrorBoundary();

    const scrollToBottom = (duration = 200) => {
        animateScroll.scrollToBottom({
            containerId: 'messages-container',
            smooth: false,
            duration: duration,
        });
    };

    const [markChatMessageAsRead] = useMutation(markChatMessageAsReadTemplate, {
        onCompleted: () => {
            setChatMessages((chatMessages) => {
                return [...chatMessages].map((message) => {
                    const messageObj = Object.assign({}, message);
                    messageObj.isRead = true;
                    return messageObj;
                });
            });
        },
        onError(error) {
            showBoundary(error);
        },
    });

    useEffect(() => {
        const showChatTemplateBtn = customerSettings.getCustomerSetting(
            currentOrganization.customerSettings,
            'CHAT_TEMPLATE',
        );
        setShowChatTemplateBtn(showChatTemplateBtn.length > 0);
        if (chatMessages.length) {
            scrollToBottom(0);
        }
    }, [chatMessages]);

    useEffect(() => {
        socket.on('NEW_CHAT_MESSAGE', ({ data }) => {
            try {
                const chatMessage = data.messageObj;
                if (chatMessage.order === order?._id) {
                    setChatMessages((actualstate) => {
                        return [...actualstate, data.messageObj];
                    });
                }
            } catch (error) {
                console.error(error);
            }
        });
        return () => {
            socket.removeAllListeners('NEW_CHAT_MESSAGE');
        };
    }, [socket]);

    useEffect(() => {
        if (chatMessages.length) {
            const hasUnreadMessages = chatMessages.some((chatMessage: ChatMessage) => !chatMessage.isRead);
            if (hasUnreadMessages) {
                const timerFunc = setTimeout(() => {
                    markChatMessageAsRead({
                        variables: {
                            orderId: order._id,
                        },
                    });
                    scrollToBottom(0);
                }, 2000);
                return () => clearInterval(timerFunc);
            }
        }
    }, [chatMessages]);

    useQuery(chatMessagesTemplate, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
        variables: {
            orderId: order._id,
        },
        onCompleted: ({ chatMessages: messages }) => {
            setChatMessages(messages);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const [sendChatMessage] = useMutation(sendChatMessageTemplate, {
        onCompleted: ({ sendChatMessage }) => {
            setChatMessages((actualstate) => {
                return [...actualstate, sendChatMessage];
            });
            setMessageValue('');
            setNoOfMessageRows(1);
        },
        onError(error) {
            showBoundary(error);
        },
        update(cache: ApolloCache<ChatMessage>, { data: { sendChatMessage } }) {
            cache.modify({
                fields: {
                    chatMessages(chatMessageRefs: Reference | (Reference | undefined)[], { toReference }) {
                        if (chatMessageRefs instanceof Array) {
                            return [...chatMessageRefs, toReference(sendChatMessage)];
                        }
                        return [chatMessageRefs, toReference(sendChatMessage)];
                    },
                },
            });
        },
    });

    const handleSendMessage = (error?: ApolloError, chatMessage?: ChatMessage) => {
        if (error) {
            setErrorMsg(translate(`common:${error.message}`));
        }
        if (chatMessage) {
            setChatMessages([...chatMessages, chatMessage]);
        }
        setShowChatTemplateDialog(false);
    };

    const onMessageChange = (event: ChangeEvent<HTMLInputElement>) => {
        setMessageValue(event.target.value);
    };

    const onSendMessage = async () => {
        const formattedMessage = getEscapedMessage(messageValue);
        const messageInput = {
            orderId: order._id,
            message: formattedMessage,
        };
        await sendChatMessage({
            variables: { ...messageInput },
        });
    };

    function openChatTemplateDialog() {
        setShowChatTemplateDialog(true);
    }

    function handleCloseDialog() {
        setShowChatTemplateDialog(false);
    }

    function showLabChatDisabledMessage() {
        if (order) {
            return (
                isSupplier &&
                !isExternalTechnician &&
                (order.isProductionFinished || order.invoicingState !== InvoicingState.NOT_READY)
            );
        }
        return false;
    }

    function showOrderInteractionDisabledMessage() {
        if (order) {
            return isCustomer && (order.isProductionFinished || order.invoicingState !== InvoicingState.NOT_READY);
        }
        return false;
    }

    function isChatDisabled() {
        if (order) {
            return isCustomer && (order.isProductionFinished || order.invoicingState !== InvoicingState.NOT_READY);
        }
        return true;
    }

    function localDentistChatDisabled() {
        return !isExternalTechnician && order?.dentist?.local;
    }

    return (
        <Paper variant="outlined" square>
            <Box p={2}>
                <Box mb={1}>
                    <Typography variant="h3">{translate('messages:CHAT')}</Typography>
                </Box>
                <Divider />

                <Box id="messages-container" pt={1} sx={{ height: 350, overflowY: 'auto' }}>
                    <Fragment>
                        {chatMessages?.map((chatMessage, index) => (
                            <Box key={index}>
                                <ChatMessageContent chatMessage={chatMessage} />
                            </Box>
                        ))}
                    </Fragment>
                    <Box id="messages-end" />
                </Box>

                <Box mb={1}>
                    <Divider />
                </Box>
                {localDentistChatDisabled() ? (
                    <Box mb={3}>
                        <Alert severity="info" sx={{ whiteSpace: 'pre-wrap' }}>
                            {translate('messages:CHAT_DISABLED_FOR_LOCAL_DENTIST_INFO')}
                        </Alert>
                    </Box>
                ) : undefined}
                {showLabChatDisabledMessage() ? (
                    <Box mb={3}>
                        <Alert severity="info" sx={{ whiteSpace: 'pre-wrap' }}>
                            {translate('messages:CHAT_DISABLED_FOR_DENTIST_INFO')}
                        </Alert>
                    </Box>
                ) : undefined}
                {showOrderInteractionDisabledMessage() ? (
                    <Box mb={3}>
                        <Alert severity="info" sx={{ whiteSpace: 'pre-wrap' }}>
                            {translate('messages:ORDER_INTERACTION_DISABLED_INFO')}
                        </Alert>
                    </Box>
                ) : undefined}
                {!isExternalTechnician && !isChatDisabled() && !localDentistChatDisabled() ? (
                    <Fragment>
                        <Box mb={1}>
                            <TextField
                                fullWidth
                                multiline
                                placeholder={translate('messages:TYPE_CHAT_MESSAGE')}
                                required
                                value={messageValue}
                                onChange={onMessageChange}
                                rows={noOfMessageRows}
                                onFocus={() => setNoOfMessageRows(6)}
                                onBlur={() => (!messageValue ? setNoOfMessageRows(1) : null)}
                                data-test="chat-message.message-input"
                            />
                        </Box>
                        <Button
                            onClick={onSendMessage}
                            disabled={!messageValue}
                            variant="contained"
                            startIcon={<SendIcon />}
                            data-test="chat-message.send-message-btn"
                        >
                            {translate('messages:SEND')}
                        </Button>
                        {showChatTemplateBtn ? (
                            <Box mt={1}>
                                <Button onClick={openChatTemplateDialog} data-test="chat-message.show-template-dialog">
                                    {translate('messages:SHOW_TEMPLATE_DIALOG')}
                                </Button>
                            </Box>
                        ) : undefined}
                    </Fragment>
                ) : undefined}
            </Box>
            <ChatTemplateDialog
                open={showChatTemplateDialog}
                onClose={handleCloseDialog}
                onSend={handleSendMessage}
                orderId={order._id}
            />
            {errorMsg ? <Alert severity="error">{errorMsg}</Alert> : undefined}
        </Paper>
    );
};

export default ChatMessages;
